import {
  RadioBlock,
  RadioBlockGroup,
} from "@faire/community/components/radio-block";
import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import { Close } from "@faire/slate-icons/Close";
import { Button } from "@faire/slate/components/button";
import { IconButton } from "@faire/slate/components/icon-button";
import { Link } from "@faire/slate/components/link";
import { LoadingSpinner } from "@faire/slate/components/loading-spinner";
import { PasswordInput } from "@faire/slate/components/password-input";
import { TextInput } from "@faire/slate/components/text-input";
import React, { useEffect, useState } from "react";

import {
  BusinessSearchInput,
  DesignNote,
  FaireWordmark,
  GoogleGlyph,
  SERIF_FONT,
  SignupShell,
  SparkleIcon,
  SparkleNote,
  StorePlacesCard,
} from "./components";
import {
  BRICK_AND_MORTAR_OPENING_BUDGETS,
  BUSINESS_PLATFORMS,
  findPlacesSuggestions,
  OPENING_YEARS,
  OPENING_SOON_DATES,
  type BusinessPlatform,
  type PlacesSuggestion,
  type Scenario,
  type SignupForm,
  shouldShowPlacesSearch,
  shouldShowPostalCode,
} from "./data";

// ---------------------------------------------------------------------------
// Shared style consts (extracted to keep className strings statically
// discoverable and under the inline-class limit).
// ---------------------------------------------------------------------------

const fieldStackStyle = "flex flex-col gap-(--slate-spacing-group-default)";
const nameRowStyle = "flex gap-(--slate-dimensions-4)";
const ctaStackStyle = "flex flex-col gap-(--slate-dimensions-3)";
const fullWidthCtaStyle = "flex w-full flex-col";
const centeredCtaStyle = "flex w-full justify-center";
const labeledFieldStyle = "flex flex-col gap-(--slate-dimensions-2)";
const centerScreenStyle =
  "flex min-h-[70vh] flex-col items-center justify-center gap-(--slate-spacing-group-default) px-(--slate-spacing-element-large) text-center";
const loadingHeaderStyle =
  "border-fs-border-muted flex h-16 w-full items-center justify-center border-b";

// Signup email modal (mirrors control's "Unlock wholesale pricing").
const modalBackdropStyle =
  "flex min-h-screen items-start justify-center bg-fs-surface-secondary px-(--slate-spacing-element-large) py-(--slate-dimensions-16)";
const modalColumnStyle =
  "flex w-full max-w-[520px] flex-col gap-(--slate-spacing-element-large)";
const modalCardStyle =
  "w-full overflow-hidden rounded-lg bg-fs-surface-primary shadow-[0_16px_48px_rgba(0,0,0,0.16)]";
const modalBodyStyle =
  "relative flex flex-col items-center gap-(--slate-spacing-group-default) px-(--slate-spacing-group-default) pt-(--slate-spacing-group-default) pb-(--slate-spacing-element-large)";
const modalFooterStyle =
  "flex flex-col gap-(--slate-dimensions-2) bg-fs-surface-secondary px-(--slate-spacing-group-default) py-(--slate-spacing-element-large)";
const googleButtonStyle =
  "flex w-full items-center justify-center gap-(--slate-dimensions-2) rounded-[4px] border border-fs-border-default bg-fs-surface-primary py-[14px]";

const FullWidthButton: React.FC<{
  children: string;
  variant?: "primary" | "secondary" | "plain";
  disabled?: boolean;
  onClick: () => void;
}> = ({ children, variant = "primary", disabled = false, onClick }) => (
  <div className={fullWidthCtaStyle}>
    <Button
      type="button"
      variant={variant}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  </div>
);

const LabeledField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "url";
  helper?: string;
  error?: string;
  onBlur?: () => void;
}> = ({ label, value, onChange, type = "text", helper, error, onBlur }) => (
  <div className={labeledFieldStyle}>
    <span className="type-fs-label-medium text-fs-text-primary">{label}</span>
    <TextInput
      aria-label={label}
      type={type}
      value={value}
      error={error}
      onBlur={onBlur}
      onChange={(event) => onChange(event.target.value)}
    />
    {helper ? (
      <span className="type-fs-label text-fs-text-subdued">{helper}</span>
    ) : null}
  </div>
);

// ---------------------------------------------------------------------------
// Chrome-only header used by the transitional screens (searching / done) that
// don't use the standard left-aligned form column.
// ---------------------------------------------------------------------------

const BareHeader: React.FC<{ progressPercent: number }> = ({
  progressPercent,
}) => (
  <>
    <header className="flex h-16 w-full items-center justify-center">
      <img
        alt="Faire"
        src="https://cdn.faire.com/static/logo.svg"
        className="h-5 w-auto object-contain"
        onError={(event) => {
          (event.target as HTMLImageElement).src =
            "https://cdn.faire.com/static/logo.svg";
        }}
      />
    </header>
    <div className="bg-fs-surface-secondary h-1 w-full">
      <div
        className="h-full bg-(--slate-color-action-surface-default) transition-[width] duration-500"
        style={{ width: `${progressPercent}%` }}
      />
    </div>
  </>
);

// ---------------------------------------------------------------------------
// Step 0 — Signup email modal. The control entry point: retailer enters their
// business email here (so email never reappears later in the flow), then lands
// on the account step.
// ---------------------------------------------------------------------------

export const EmailModalStep: React.FC<{
  scenario: Scenario;
  onSubmit: (email: string) => void;
}> = ({ scenario, onSubmit }) => {
  const [email, setEmail] = useState(scenario.email);
  const onNext = () => onSubmit(email);

  return (
    <div className={modalBackdropStyle}>
      <div className={modalColumnStyle}>
        <div className={modalCardStyle}>
          <div className={modalBodyStyle}>
            <div className="absolute top-3 right-3">
              <IconButton
                Icon={Close}
                variant="plain"
                size="small"
                shape="circle"
                aria-label="Close"
                onClick={() => undefined}
              />
            </div>
            <FaireWordmark className="h-5 w-auto object-contain" />
            <h1
              className="text-fs-text-primary text-[30px] leading-[38px]"
              style={{ fontFamily: SERIF_FONT }}
            >
              Unlock wholesale pricing
            </h1>
            <div className="flex w-full flex-col gap-(--slate-spacing-element-default)">
              <TextInput
                label="Business email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <FullWidthButton onClick={onNext}>
                Sign up for free
              </FullWidthButton>
              <button
                type="button"
                className={googleButtonStyle}
                onClick={onNext}
              >
                <GoogleGlyph className="h-5 w-5" />
                <span className="type-fs-paragraph-medium text-fs-text-primary">
                  Sign up with Google
                </span>
              </button>
              <p className="type-fs-label text-fs-text-subdued">
                By proceeding, you agree to our{" "}
                <Link
                  linkTo="https://www.faire.com/legal/terms"
                  target="_blank"
                >
                  Terms
                </Link>{" "}
                and{" "}
                <Link
                  linkTo="https://www.faire.com/about/privacy"
                  target="_blank"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>
          <div className={modalFooterStyle}>
            <span className="type-fs-label text-fs-text-subdued">
              Are you a brand?{" "}
              <Link linkTo="https://www.faire.com/brand-signup" target="_blank">
                Sign up to sell
              </Link>
            </span>
            <span className="type-fs-label text-fs-text-subdued">
              Already have an account?{" "}
              <Link linkTo="https://www.faire.com/login" target="_blank">
                Sign in
              </Link>
            </span>
          </div>
        </div>
        <DesignNote title="Try an email">
          The address you type here decides what we manage to resolve.{" "}
          <strong>jeff@jeffswrenches.com</strong> and{" "}
          <strong>createdbyherboutique@gmail.com</strong> both hit a Google
          Places listing. <strong>nadia@thelittlepantry.co</strong> resolves a
          website but no Places listing. Anything else — a plain{" "}
          <strong>@gmail.com</strong> address, say — finds nothing and drops
          straight into the standard questions.
        </DesignNote>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 1 — Account details (the trigger). Mirrors the real signup step; email
// is already captured in the modal, so this only asks for name + password. The
// domain disclosure lives at the bottom.
// ---------------------------------------------------------------------------

export const PersonalInfoStep: React.FC<{
  scenario: Scenario;
  progressPercent: number;
  onBack: () => void;
  onNext: () => void;
}> = ({ scenario, progressPercent, onBack, onNext }) => {
  const [firstName, setFirstName] = useState(scenario.firstName);
  const [lastName, setLastName] = useState(scenario.lastName);
  const [password, setPassword] = useState("wholesale2026");

  return (
    <SignupShell
      progressPercent={progressPercent}
      title="Welcome! Let's get started"
      subcopy="Get 50% off your first order—plus free returns on every brand."
      onBack={onBack}
    >
      <div className={fieldStackStyle}>
        <div className={nameRowStyle}>
          <TextInput
            label="First name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
          <TextInput
            label="Last name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </div>
        <PasswordInput
          label="Password"
          helper="8 characters minimum"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>
      <div className={ctaStackStyle}>
        <FullWidthButton onClick={onNext}>Next</FullWidthButton>
      </div>
    </SignupShell>
  );
};

// ---------------------------------------------------------------------------
// Step 2 — Loading. Auto-advances from the parent. Every scraped domain sees
// this, including the ones the lookup comes back empty on.
// ---------------------------------------------------------------------------

export const SearchingStep: React.FC = () => {
  const [beat, setBeat] = useState(0);
  const title = usePlaygroundLocalMsg("Getting things ready…");
  const verifyingMessage = usePlaygroundLocalMsg("Verifying your information.");
  const settingUpMessage = usePlaygroundLocalMsg("Setting up your account.");
  // Deliberately says nothing about what we found: the same beat runs whether
  // the lookup resolves a listing, a bare website, or nothing at all.
  const supportingMessages = [verifyingMessage, settingUpMessage];

  useEffect(() => {
    const timer = setInterval(() => {
      setBeat((previousBeat) =>
        Math.min(previousBeat + 1, supportingMessages.length - 1)
      );
    }, 2000);
    return () => clearInterval(timer);
  }, [supportingMessages.length]);

  return (
    <div className="bg-fs-surface-primary min-h-screen">
      <header className={loadingHeaderStyle}>
        <FaireWordmark />
      </header>
      <div className={centerScreenStyle}>
        <LoadingSpinner size="xLarge" />
        <div className="flex flex-col gap-(--slate-dimensions-2)">
          <h1
            className="text-fs-text-primary text-[30px] leading-[38px]"
            style={{ fontFamily: SERIF_FONT }}
          >
            {title}
          </h1>
          <p className="type-fs-paragraph text-fs-text-subdued">
            {supportingMessages[beat]}
          </p>
        </div>
      </div>
    </div>
  );
};

export type ConfirmedStoreDetails = Pick<
  SignupForm,
  "storeName" | "website" | "postalCode"
>;

// ---------------------------------------------------------------------------
// Confirm store. A Places match shows corroborating identity details; a
// website-only match stays editable. Business type is already answered by the
// time we get here, so ZIP is either required (physical store) or unnecessary.
// ---------------------------------------------------------------------------

/** How the website-only confirm card treats ZIP. */
export type PostalCodeMode = "required" | "hidden";

const CONFIRM_SUBCOPY =
  "We've finished checking your website and have saved you some time. Just a few more steps to set up your account.";

export const ConfirmStoreStep: React.FC<{
  scenario: Scenario;
  title: string;
  progressPercent: number;
  postalCodeMode: PostalCodeMode;
  onBack: () => void;
  onConfirm: (
    isMine: boolean,
    confirmedStoreDetails?: ConfirmedStoreDetails
  ) => void;
}> = ({
  scenario,
  title,
  progressPercent,
  postalCodeMode,
  onBack,
  onConfirm,
}) => {
  const match = scenario.match;
  const [storeName, setStoreName] = useState(match?.storeName ?? "");
  const [website, setWebsite] = useState(match?.website ?? "");
  const [postalCode, setPostalCode] = useState(match?.postalCode ?? "");
  const [hasTouchedPostalCode, setHasTouchedPostalCode] = useState(false);
  const requiredZipLabel = usePlaygroundLocalMsg("ZIP code");
  const invalidZipMessage = usePlaygroundLocalMsg(
    "Please enter a valid zip code for the US."
  );
  const rejectionLabel = usePlaygroundLocalMsg("I’ll enter my store details");

  if (!match) {
    return null;
  }

  const isPostalCodeRequired =
    match.source === "website-only" && postalCodeMode === "required";
  const isPostalCodeValid = /^\d{5}(-\d{4})?$/.test(postalCode.trim());
  const postalCodeError =
    isPostalCodeRequired && hasTouchedPostalCode && !isPostalCodeValid
      ? invalidZipMessage
      : undefined;
  const handleContinue = () => {
    if (isPostalCodeRequired && !isPostalCodeValid) {
      setHasTouchedPostalCode(true);
      return;
    }
    onConfirm(true, { storeName, website, postalCode });
  };

  return (
    <SignupShell
      progressPercent={progressPercent}
      title={title}
      subcopy={<SparkleNote>{CONFIRM_SUBCOPY}</SparkleNote>}
      onBack={onBack}
    >
      {match.source === "google-places" ? (
        <StorePlacesCard profile={match} />
      ) : (
        <div className={fieldStackStyle}>
          <LabeledField
            label="Business name"
            value={storeName}
            onChange={setStoreName}
          />
          <LabeledField
            label="Website"
            type="url"
            value={website}
            onChange={setWebsite}
          />
          {isPostalCodeRequired ? (
            <LabeledField
              label={requiredZipLabel}
              value={postalCode}
              onChange={setPostalCode}
              error={postalCodeError}
              onBlur={() => setHasTouchedPostalCode(true)}
            />
          ) : null}
        </div>
      )}
      <div className={ctaStackStyle}>
        <FullWidthButton onClick={handleContinue}>Continue</FullWidthButton>
        <div className={centeredCtaStyle}>
          <Button
            type="button"
            variant="plain"
            onClick={() => onConfirm(false)}
          >
            {rejectionLabel}
          </Button>
        </div>
      </div>
    </SignupShell>
  );
};

// ---------------------------------------------------------------------------
// Opening year. First standard question after a declined match, so it carries
// the bridge copy in the standard order.
// ---------------------------------------------------------------------------

export const OpeningYearStep: React.FC<{
  form: SignupForm;
  progressPercent: number;
  showDeclinedMatchMessage: boolean;
  onBack: () => void;
  onChange: (value: string) => void;
  onNext: () => void;
}> = ({
  form,
  progressPercent,
  showDeclinedMatchMessage,
  onBack,
  onChange,
  onNext,
}) => {
  const declinedMatchMessage = usePlaygroundLocalMsg(
    "We’ll ask a few quick questions instead."
  );

  return (
    <SignupShell
      progressPercent={progressPercent}
      title="When did your business open?"
      subcopy={showDeclinedMatchMessage ? declinedMatchMessage : undefined}
      onBack={onBack}
    >
      <RadioBlockGroup
        name="opening-year"
        columns={2}
        aria-label="Opening year"
      >
        {OPENING_YEARS.map((year) => (
          <RadioBlock
            key={year}
            id={`opening-year-${year}`}
            name="opening-year"
            value={year}
            label={year}
            checked={form.openingYear === year}
            onChange={() => onChange(year)}
          />
        ))}
      </RadioBlockGroup>
      <div className={ctaStackStyle}>
        <FullWidthButton onClick={onNext}>Next step</FullWidthButton>
      </div>
    </SignupShell>
  );
};

const AutoAdvanceRadioOption: React.FC<{
  groupName: string;
  value: string;
  defaultMessage: string;
  checked: boolean;
  onSelect: (value: string) => void;
}> = ({ groupName, value, defaultMessage, checked, onSelect }) => {
  const label = usePlaygroundLocalMsg(defaultMessage);

  return (
    <RadioBlock
      id={`${groupName}-${value}`}
      name={groupName}
      value={value}
      label={label}
      checked={checked}
      onChange={() => onSelect(value)}
    />
  );
};

export const OpeningSoonDateStep: React.FC<{
  form: SignupForm;
  progressPercent: number;
  onBack: () => void;
  onSelect: (value: string) => void;
}> = ({ form, progressPercent, onBack, onSelect }) => {
  const title = usePlaygroundLocalMsg("When are you planning to open?");

  return (
    <SignupShell
      progressPercent={progressPercent}
      title={title}
      onBack={onBack}
    >
      <RadioBlockGroup name="opening-soon-date" columns={1}>
        {OPENING_SOON_DATES.map((option) => (
          <AutoAdvanceRadioOption
            key={option}
            groupName="opening-soon-date"
            value={option}
            defaultMessage={option}
            checked={form.openingSoonDate === option}
            onSelect={onSelect}
          />
        ))}
      </RadioBlockGroup>
    </SignupShell>
  );
};

export const OpeningBudgetStep: React.FC<{
  form: SignupForm;
  progressPercent: number;
  onBack: () => void;
  onSelect: (value: string) => void;
}> = ({ form, progressPercent, onBack, onSelect }) => {
  const title = usePlaygroundLocalMsg("What’s your opening inventory budget?");
  const subtitle = usePlaygroundLocalMsg(
    "Include what you’ve spent and plan to spend before opening. This helps us guide you."
  );

  return (
    <SignupShell
      progressPercent={progressPercent}
      title={title}
      subcopy={subtitle}
      onBack={onBack}
    >
      <RadioBlockGroup name="opening-budget" columns={1}>
        {BRICK_AND_MORTAR_OPENING_BUDGETS.map((option) => (
          <AutoAdvanceRadioOption
            key={option}
            groupName="opening-budget"
            value={option}
            defaultMessage={option}
            checked={form.openingBudget === option}
            onSelect={onSelect}
          />
        ))}
      </RadioBlockGroup>
    </SignupShell>
  );
};

// ---------------------------------------------------------------------------
// "Which best describes your business?" — the first question after account
// details. Its answer tells the confirm card whether a website-only match owes
// us a ZIP before the lookup beat begins.
// ---------------------------------------------------------------------------

export const BusinessTypeStep: React.FC<{
  form: SignupForm;
  title: string;
  progressPercent: number;
  onBack: () => void;
  onToggle: (platform: BusinessPlatform) => void;
  onNext: () => void;
}> = ({ form, title, progressPercent, onBack, onToggle, onNext }) => {
  const isNextDisabled = form.businessPlatforms.length === 0;

  return (
    <SignupShell
      progressPercent={progressPercent}
      title={title}
      onBack={onBack}
    >
      <RadioBlockGroup
        name="business-platforms"
        columns={1}
        variant="multiselect"
        aria-label="Business type"
      >
        {BUSINESS_PLATFORMS.map((option) => (
          <RadioBlock
            key={option.value}
            id={`business-platform-${option.value}`}
            name="business-platforms"
            value={option.value}
            label={option.label}
            description={option.subtext}
            checked={form.businessPlatforms.includes(option.value)}
            onChange={() => onToggle(option.value)}
          />
        ))}
      </RadioBlockGroup>
      <div className={ctaStackStyle}>
        <FullWidthButton disabled={isNextDisabled} onClick={onNext}>
          Next
        </FullWidthButton>
      </div>
    </SignupShell>
  );
};

// ---------------------------------------------------------------------------
// Declined path — "What's your business name?". Production (`StoreInfoForm`)
// swaps the input based on the business-type answer: brick and mortar gets the
// Google Places search, everything else gets a plain text field. Postal code is
// revealed for a physical store that typed its name manually, and always for a
// temporary-location-only business.
// ---------------------------------------------------------------------------

export const BusinessNameStep: React.FC<{
  form: SignupForm;
  isOpeningSoon: boolean;
  showDeclinedMatchMessage: boolean;
  progressPercent: number;
  onBack: () => void;
  onChangeName: (value: string) => void;
  onChangePostalCode: (value: string) => void;
  onSelectSuggestion: (suggestion: PlacesSuggestion) => void;
  onEnterManually: () => void;
  onNext: () => void;
}> = ({
  form,
  isOpeningSoon,
  showDeclinedMatchMessage,
  progressPercent,
  onBack,
  onChangeName,
  onChangePostalCode,
  onSelectSuggestion,
  onEnterManually,
  onNext,
}) => {
  const [hasBlurred, setHasBlurred] = useState(false);
  const declinedMatchMessage = usePlaygroundLocalMsg(
    "We’ll ask a few quick questions instead."
  );
  const standardTitle = usePlaygroundLocalMsg("What’s your business name?");
  const openingSoonTitle = usePlaygroundLocalMsg(
    "Do you have a name for your business yet?"
  );
  const openingSoonSubtitle = usePlaygroundLocalMsg(
    "Don’t worry, you can change this later."
  );
  const showPlacesSearch = shouldShowPlacesSearch(form.businessPlatforms);
  const showPostalCode = shouldShowPostalCode(
    form.businessPlatforms,
    form.isManualBusinessName
  );
  const isEmpty = form.storeName.trim().length === 0;
  const error =
    hasBlurred && isEmpty ? "Please enter your business name" : undefined;

  return (
    <SignupShell
      progressPercent={progressPercent}
      title={isOpeningSoon ? openingSoonTitle : standardTitle}
      subcopy={
        isOpeningSoon
          ? openingSoonSubtitle
          : showDeclinedMatchMessage
            ? declinedMatchMessage
            : undefined
      }
      onBack={onBack}
    >
      <div className={fieldStackStyle}>
        {showPlacesSearch ? (
          <BusinessSearchInput
            label="Business name"
            placeholder="Search your business name"
            value={form.storeName}
            error={error}
            suggestions={findPlacesSuggestions(form.storeName)}
            onChange={onChangeName}
            onSelectSuggestion={onSelectSuggestion}
            onEnterManually={onEnterManually}
          />
        ) : (
          <div className={labeledFieldStyle}>
            <TextInput
              label="Business name"
              value={form.storeName}
              error={error}
              onChange={(event) => onChangeName(event.target.value)}
              onBlur={() => setHasBlurred(true)}
            />
          </div>
        )}
        {showPostalCode ? (
          <TextInput
            label="ZIP code"
            value={form.postalCode}
            onChange={(event) => onChangePostalCode(event.target.value)}
          />
        ) : null}
      </div>
      <div className={ctaStackStyle}>
        <FullWidthButton disabled={isEmpty} onClick={onNext}>
          Next
        </FullWidthButton>
      </div>
    </SignupShell>
  );
};

// ---------------------------------------------------------------------------
// Done. Simple success screen so the flow has a resting state.
// ---------------------------------------------------------------------------

export const DoneStep: React.FC<{
  scenario: Scenario;
  progressPercent: number;
  onRestart: () => void;
}> = ({ scenario, progressPercent, onRestart }) => (
  <div className="bg-fs-surface-primary min-h-screen">
    <BareHeader progressPercent={progressPercent} />
    <div className={centerScreenStyle}>
      <div className="bg-fs-surface-secondary flex h-16 w-16 items-center justify-center rounded-full">
        <SparkleIcon className="text-fs-text-primary h-8 w-8" />
      </div>
      <div className="flex flex-col gap-(--slate-dimensions-2)">
        <p className="type-fs-display-s text-fs-text-primary">
          You're all set, {scenario.firstName}
        </p>
        <p className="type-fs-paragraph text-fs-text-subdued">
          Your account is ready. Start shopping wholesale from brands you'll
          love.
        </p>
      </div>
      <div className="w-full max-w-[320px]">
        <FullWidthButton onClick={onRestart}>
          Go to the marketplace
        </FullWidthButton>
      </div>
    </div>
  </div>
);
